/* ---------------------------------------------------------------------------
   Menú lateral: datos + filtrado por perfil.

   Vive fuera de `AppSidebar` para poder probar el FILTRADO como función pura:
   dentro del componente, el único caso que el menú real ejercita es "grupo y
   sus hijos son todos /admin", que esconde todo por igual y no distingue un
   filtro que mira los hijos de uno que se olvida de ellos.

   REGLA (ver webapp/CLAUDE.md): un ítem con submódulos NO navega. Es un rótulo
   de módulo ("Mantenedor de …") que solo abre y cierra su lista; las pantallas
   del módulo son sus hijos. Por eso `GrupoDeMenu` no tiene `url`: la regla es
   estructural, no una convención que se pueda olvidar al agregar un ítem.

   REGLA: el menú declara NAVEGACIÓN, no el inventario de pantallas. Una
   pantalla puede existir sin fila (Dashboard y Configuración hoy: rutas vivas,
   sin acceso desde acá) y un acceso puede no ser una fila (`ACCESO_RAPIDO`, que
   se dibuja como botón). Sacar el ítem NO es sacar la ruta.
--------------------------------------------------------------------------- */

import { seccionDeclarada } from "@/lib/origenDeNavegacion";
import type { PerfilDelUsuario } from "@/lib/auth";
import { puedeAcceder } from "@/lib/permisos";

/** Destino del menú: una fila que navega a su pantalla. */
export interface EnlaceDeMenu {
  label: string;
  url: string;
}

/** Rótulo de módulo: NO navega, solo despliega sus pantallas. Sin `url`. */
export interface GrupoDeMenu {
  label: string;
  hijos: EnlaceDeMenu[];
}

export type ItemDeMenu = EnlaceDeMenu | GrupoDeMenu;

export function esGrupo(item: ItemDeMenu): item is GrupoDeMenu {
  return "hijos" in item;
}

/** Clave estable para listas de React: los rótulos del menú son únicos. */
export function claveDeItem(item: ItemDeMenu): string {
  return esGrupo(item) ? item.label : item.url;
}

/* Acceso rápido: crear un informe es la ACCIÓN de arranque del proceso, no un
   destino más de la navegación. Por eso no entra en `NAV_ITEMS` — se dibuja
   como botón, arriba y por sobre el resto del menú (ver `AppSidebar`).
   Se declara acá igual que los demás destinos para que el filtro por perfil lo
   alcance con la misma regla, y no quede un botón que la guarda va a cerrar. */
export const ACCESO_RAPIDO: EnlaceDeMenu = {
  label: "Crear informe",
  url: "/informes/nuevo",
};

/* Segundo acceso rápido, y por la MISMA razón que el primero: enlazar
   referencias de mercado a un informe es una ACCIÓN sobre un informe que ya
   existe, no un destino más de la navegación ni un mantenedor. Ponerlo como
   fila lo dejaría entre pantallas que se abren para mirar, cuando esto se abre
   para hacer algo y volver.

   Va SEGUNDO y con menos peso visual: crear un informe arranca el proceso;
   esto es un paso de adentro. Por eso el botón de arriba es el único sólido y
   este lleva borde. */
export const ACCESO_SECUNDARIO: EnlaceDeMenu = {
  label: "Enlazar referencias",
  url: "/referencias",
};

/* Bloque superior: la navegación de trabajo del día a día. "Informes" NO es un
   grupo — su único destino de menú es "Informes en proceso", así que va como
   fila plana. Crear informe salió de ahí a `ACCESO_RAPIDO`, y editar se abre
   desde el listado: un rótulo desplegable sobre una sola fila escondía la
   pantalla sin agrupar nada.

   La fila "Inicio" ya no existe: la portada de accesos se eliminó porque era
   una escala entre el login y el trabajo, y "/" ahora redirige acá (ver
   `App.tsx`). Por eso "Informes en proceso" encabeza el menú — es la primera
   pantalla que ve el usuario al entrar, no la segunda. */
export const NAV_ITEMS: ItemDeMenu[] = [
  { label: "Informes en proceso", url: "/informes" },
  /* Bandeja del revisor (Módulo G, RF-G1). Fila plana y hermana del listado,
     no hija suya: es otra cola de trabajo, con otro criterio de orden y otro
     dueño, no una vista filtrada de aquella.

     NO se filtra por perfil, y eso es correcto: RF-G1 restringe QUÉ se ve
     dentro de la bandeja —la API la acota al usuario de la sesión—, no quién
     entra. La abren los tres perfiles y cada uno recibe su propia cola. */
  { label: "Revisiones pendientes", url: "/revisiones" },
];

/* Bloque inferior: lo que no es el trabajo del día a día. Primero lo que abre
   cualquier perfil (Novedades y Feedback) y debajo los mantenedores, que son de
   Super Admin — el pie va de lo más general a lo más restringido.

   El Design System NO está acá: es referencia interna de UI, no navegación de
   producto. Su ruta sigue viva y se llega pegando `/design-system` en la
   barra: al eliminarse la portada desapareció su única tarjeta, y meterlo en
   el menú contradiría la razón por la que nunca estuvo. */
export const FOOTER_ITEMS: ItemDeMenu[] = [
  { label: "Novedades", url: "/novedades" },
  { label: "Feedback", url: "/feedback" },
  {
    // Módulos B y C del spec: el listado de usuarios y su submódulo Perfiles
    // son DOS pantallas del mismo mantenedor. El rótulo las agrupa; no es una
    // de ellas.
    label: "Mantenedor de Usuarios",
    hijos: [
      { label: "Usuarios", url: "/admin/usuarios" },
      { label: "Perfiles", url: "/admin/perfiles" },
    ],
  },
  {
    // Módulo D del spec y sus dos catálogos hermanos: qué secciones tiene el
    // informe (contenidos), de dónde salen sus datos (contextos) y qué se
    // verifica al visarlo (revisión). Son TRES pantallas del mismo
    // mantenedor, así que el rótulo las agrupa y no es ninguna de ellas —
    // misma regla que el Mantenedor de Usuarios.
    label: "Mantenedor de Informes",
    hijos: [
      { label: "Contenidos", url: "/admin/contenidos" },
      { label: "Contextos", url: "/admin/contextos" },
      { label: "Revisión de informes", url: "/admin/revision-informes" },
    ],
  },
  /* GRUPO desde que sumó "Consumo y costos" (odd/tasks/consumo-de-ia.md): la
     regla del menú es "más de un destino → grupo", y antes era fila plana
     con un solo destino. El listado principal (el prompt y el modelo de
     cada función) entra como HIJO "Funciones", no como el rótulo — misma
     forma que el Mantenedor de Usuarios.

     Va donde iba la fila plana, por la misma regla que la ordenaba: es el
     más interno de los tres primeros mantenedores, el que configura cómo se
     comporta la IA y no el trabajo que se hace con ella. */
  {
    label: "Mantenedor de IA",
    hijos: [
      { label: "Funciones", url: "/admin/ia" },
      { label: "Laboratorio de IA", url: "/admin/ia/laboratorio" },
      { label: "Consumo y costos", url: "/admin/ia/consumo" },
    ],
  },
  /* Fila plana por lo mismo que la de IA: un solo destino. Las dos mitades del
     contenido —textos e ítems— son pestañas dentro de la pantalla y no dos
     filas, porque quien viene a corregir la metodología suele mirar también las
     condiciones de pago.

     Va DESPUÉS de la de IA: configura el contenido de un documento comercial
     concreto, que es todavía más interno que cómo se comporta la IA. */
  { label: "Mantenedor de cotización", url: "/admin/cotizacion" },
  /* Fila plana por lo mismo que las dos de arriba: un solo destino.

     Va ÚLTIMA porque es la más interna de todas: no configura una pantalla ni
     un documento, sino el dato de identidad de la empresa que va impreso en el
     pie de cada página de cada cotización. Es lo que menos se toca. */
  /* Fila plana por lo mismo que las de arriba: un solo destino.

     Va DESPUÉS de la de cotización y ANTES de los datos de la empresa: no
     configura un documento comercial sino el MÉTODO con que se tasa, que es
     más interno que aquel y menos que la identidad de la empresa. */
  { label: "Tablas de homologación", url: "/admin/homologacion" },
  /* Fila plana por lo mismo que las de arriba: un solo destino.

     Va JUNTO a homologación porque es lo mismo que aquella: vocabulario del
     método con que se tasa. Aquella fija cuánto corrige cada diferencia; esta,
     con qué se nombran los suelos de un predio. */
  { label: "Clases de uso de suelo", url: "/admin/clases-de-suelo" },
  { label: "Datos de la empresa", url: "/admin/empresa" },
];

/**
 * Deja solo lo que el perfil activo puede abrir — enlaces Y submódulos.
 *
 * El filtro entra a los hijos a propósito: son destinos como cualquier otro y
 * un grupo visible no autoriza nada sobre ellos. Ofrecer una puerta que la
 * guarda va a cerrar es la otra mitad del defecto que `RequiereRol` arregla
 * del lado de la ruta.
 *
 * Un grupo sin hijos visibles DESAPARECE: como no navega a ninguna parte, si
 * se dibujara igual sería una fila que no hace nada al hacerle clic.
 */
export function filtrarItems(
  items: ItemDeMenu[],
  perfil: PerfilDelUsuario | null | undefined,
): ItemDeMenu[] {
  return items.flatMap<ItemDeMenu>((item) => {
    if (!esGrupo(item)) return puedeAcceder(perfil, item.url) ? [item] : [];

    const hijos = item.hijos.filter((hijo) => puedeAcceder(perfil, hijo.url));
    return hijos.length ? [{ ...item, hijos }] : [];
  });
}

/**
 * TODA url que el menú declara como destino propio: filas, hijos de grupo y el
 * acceso rápido. Sin filtrar por perfil a propósito — describe la ESTRUCTURA
 * del menú, no lo que un rol ve, y quien la usa (`esRutaActiva`) no decide
 * permisos.
 */
export const DESTINOS_DECLARADOS: ReadonlySet<string> = new Set([
  ACCESO_RAPIDO.url,
  ACCESO_SECUNDARIO.url,
  ...[...NAV_ITEMS, ...FOOTER_ITEMS].flatMap((item) =>
    esGrupo(item) ? item.hijos.map((hijo) => hijo.url) : [item.url],
  ),
]);

/**
 * ¿La fila de `url` va resaltada estando en `pathname`?
 *
 * Manda el ORIGEN DECLARADO cuando lo hay. Hay pantallas compartidas cuya ruta
 * ya no dice en qué sección se está trabajando: `/informes/7` es un informe de
 * la cola del ejecutivo o uno que el revisor está corrigiendo desde su bandeja,
 * y son la misma url. Con el prefijo a secas, el revisor veía encendida
 * "Informes en proceso" mientras el breadcrumb decía "Revisiones pendientes"
 * — dos afirmaciones distintas sobre dónde está parado.
 *
 * Sin origen declarado vale la regla de siempre: una fila se enciende por su
 * ruta exacta o por las que cuelgan de ella. `/informes/7` sin más contexto
 * sigue siendo "ver un informe DE la cola".
 *
 * La excepción: una ruta que ES OTRO DESTINO DECLARADO no le pertenece a la
 * fila padre. `/informes/nuevo` cuelga de `/informes` en la URL, pero se llega
 * por el botón de acceso rápido, así que encender "Informes en proceso" al
 * crear un informe señalaba una fila que el usuario no tocó. Comparte prefijo,
 * no procedencia.
 */
export function esRutaActiva(pathname: string, url: string, search = ""): boolean {
  const seccion = seccionDeclarada(search);
  // Excluyente: con la sección declarada, ninguna otra fila se enciende — ni
  // siquiera la que gana por prefijo. Si las dos pudieran, habría dos filas
  // resaltadas a la vez.
  if (seccion) return url === seccion;

  if (pathname === url) return true;
  if (DESTINOS_DECLARADOS.has(pathname)) return false;
  return pathname.startsWith(url + "/");
}
